import { find } from "lodash";
import { hasItems } from "../../../../helpers/utils";
import AppTheme from "../../AppTheme";

const WEEKLY_HOURS = 168;
const MAX_REFLECTION_DEPTH = 20;

const isTotalHours = (item) => {
  return item.dayIndex == 0; //changed 1 to 0 due to reversing hours in ReflectionList
};

const addAllParents = (a, activities, setIds) => {
  if (a != null && a.parents != null) {
    setIds.push(...a.parents);
    a.parents.forEach((p) => {
      addAllParents(activities[p], activities, setIds);
    });
  }
};

const getActivities = (templateActivities, activities, multiLevel) => {
  const activitiesBySet = {};
  (templateActivities || []).forEach((ta) => {
    const idElement = ta.id_activity;
    let linkedElementId = idElement + "";
    const isMisc = linkedElementId.endsWith("_misc");
    if (isMisc) {
      linkedElementId = linkedElementId.substring(
        0,
        linkedElementId.length - 5,
      );
    }
    const act = activities[linkedElementId];
    if (act != null) {
      const setIds = [];
      if (isMisc) {
        setIds.push(idElement.replace("_misc", ""));
      } else if (act.parents) {
        if (multiLevel) {
          setIds.push(...act.parents);
        } else {
          addAllParents(act, activities, setIds);
        }
      }
      if (setIds.length === 0) {
        setIds.push(ta.is_uncategorized ? "uncategorized" : "other");
      }

      setIds
        .filter((value, index, self) => self.indexOf(value) === index)
        .forEach((setId) => {
          let actList = activitiesBySet[setId] || [];

          actList.push({
            item_type: "activity",
            item_id: idElement,
            name: act.name + (isMisc ? " (Generic)" : ""),
            color: act.color,
            icon: act.icon,
            is_uncategorized: ta.is_uncategorized,
            is_modified: ta.is_modified,
            days: [
              {
                hours: ta.hours || 0,
                is_modified: !isTotalHours(ta),
                index: 0,
              },
              {
                hours: ta.total_hours || 0,
                is_modified: isTotalHours(ta),
                index: 1,
              },
            ],
          });

          activitiesBySet[setId] = actList;
        });
    }
  });

  Object.values(activitiesBySet).forEach((actList) => {
    actList.sort((a, b) => b.days[0].hours - a.days[0].hours);
  });

  return activitiesBySet;
};

const getSet = (sets, setKey) => {
  if (["other", "uncategorized"].includes(setKey)) {
    return {
      item_id: setKey,
      name: setKey,
      color: AppTheme.almostBlack,
      icon: "2753",
    };
  } else if (setKey === "resolutions") {
    return {
      item_id: setKey,
      name: "Goals",
      color: AppTheme.almostBlack,
      icon: "1f3c6",
    };
  }

  return sets[setKey];
};

function deepLinkToParents(activitiesBySet, firstLevel, activities) {
  const toLink = Object.keys(activitiesBySet);
  Object.values(firstLevel).forEach((setKey) => {
    let set = getSet(activities, setKey);
    if (
      set != null &&
      activitiesBySet[setKey] != null &&
      activitiesBySet[setKey].length > 0
    ) {
      const index = toLink.indexOf(setKey);
      if (index > -1) toLink.splice(index, 1);
    }
  });

  const MAX_ITERATIONS = 100;
  let hadChanges = true;
  let iter = 0;
  while (hadChanges) {
    iter++;
    if (iter > MAX_ITERATIONS) break;

    hadChanges = false;
    const toAdd = [];
    const toRemove = [];

    toLink.forEach((id) => {
      const act = activities[id];
      if (act != null && act.parents != null && act.parents.length > 0) {
        act.parents.forEach((p) => {
          if (activitiesBySet[p] == null) {
            if (!firstLevel.includes(p)) toAdd.push(p);
            activitiesBySet[p] = [];
          }

          activitiesBySet[id].forEach((a) => {
            //console.log("Link " + activities[id].name + " to " + activities[p].name);
            if (
              activitiesBySet[p].filter((b) => b.item_id == a.item_id).length ==
              0
            ) {
              activitiesBySet[p].push(a);
            }
          });
        });
        hadChanges = true;
        toRemove.push(id);
      }
    });

    toAdd.forEach((id) => {
      toLink.push(id);
    });
    toRemove.forEach((id) => {
      const index = toLink.indexOf(id);
      if (index > -1) toLink.splice(index, 1);
    });
  }

  toLink.forEach((id) => {
    const p = "other";
    if (activitiesBySet[p] == null) {
      activitiesBySet[p] = [];
    }
    activitiesBySet[id].forEach((a) => {
      if (
        activitiesBySet[p].filter((b) => b.item_id == a.item_id).length == 0
      ) {
        activitiesBySet[p].push(a);
      }
    });
  });
}

function linkToParents(activitiesBySet, firstLevel, activities) {
  const toLink = Object.keys(activitiesBySet);
  Object.values(firstLevel).forEach((setKey) => {
    let set = getSet(activities, setKey);
    if (
      set != null &&
      activitiesBySet[setKey] != null &&
      activitiesBySet[setKey].length > 0
    ) {
      const index = toLink.indexOf(setKey);
      if (index > -1) toLink.splice(index, 1);
    }
  });

  let hadChanges = true;
  let depth = 1;
  while (hadChanges) {
    hadChanges = false;
    depth++;
    if (depth > MAX_REFLECTION_DEPTH) break;
    const toAdd = [];
    const toRemove = [];

    toLink.forEach((id) => {
      const act = activities[id];
      if (act != null && act.parents != null && act.parents.length > 0) {
        act.parents.forEach((p) => {
          if (activitiesBySet[p] == null) {
            if (!firstLevel.includes(p)) toAdd.push(p);
            activitiesBySet[p] = [];
          }

          activitiesBySet[id].forEach((a) => {
            if (
              activitiesBySet[p].filter((b) => b.item_id == a.item_id).length ==
              0
            ) {
              activitiesBySet[p].push(a);
            }
          });
        });
        toRemove.push(id);
        hadChanges = true;
      }
    });

    toAdd.forEach((id) => {
      toLink.push(id);
    });
    toRemove.forEach((id) => {
      const index = toLink.indexOf(id);
      if (index > -1) toLink.splice(index, 1);
    });
  }

  toLink.forEach((id) => {
    const p = "other";
    if (activitiesBySet[p] == null) {
      activitiesBySet[p] = [];
    }
    activitiesBySet[id].forEach((a) => {
      if (
        activitiesBySet[p].filter((b) => b.item_id == a.item_id).length == 0
      ) {
        activitiesBySet[p].push(a);
      }
    });
  });
}

function hasChild(group, id, parsed = []) {
  if (parsed.includes(group.item_id)) return false;

  const newParsed = parsed.slice(0);
  newParsed.push(group.item_id);

  if (group.item_id == id) {
    return true;
  } else if (group.children) {
    for (var i in group.children) {
      if (hasChild(group.children[i], id, newParsed)) return true;
    }
    return false;
  }
}

function cleanupChildren(group, parsed = []) {
  if (group.children) {
    if (parsed.includes(group.item_id)) return;

    const newParsed = parsed.slice(0);
    const toRemove = [];
    for (var i in group.children) {
      const child = group.children[i];
      const targetId = child.item_id;
      let alreadyExists = false;
      for (var j in group.children) {
        if (i != j && group.children) {
          alreadyExists = hasChild(group.children[j], targetId);
          if (alreadyExists) break;
        }
      }
      newParsed.push(child.item_id);
      if (alreadyExists) toRemove.unshift(i); //need to splice in reverse order
      cleanupChildren(child, newParsed);
    }
    toRemove.forEach((i) => {
      group.children.splice(i, 1);
    });
  }
}

function addVisible(group, visibleItems, addedItems) {
  if (group != null && group.item_id != null) {
    visibleItems.push(group.item_id);
    if (group.children != null && !addedItems.includes(group.item_id)) {
      let newAddedItems = [...addedItems];
      newAddedItems.push(group.item_id);
      group.children.forEach((c) => addVisible(c, visibleItems, newAddedItems));
    }
  }
}

const getTemplateDataModel = (
  templateData,
  allActivities = [],
  rootElements = null,
  multiLevel = true,
) => {
  const activities = {};
  allActivities.forEach((a) => (activities[a.id] = a));
  const activitiesBySet = getActivities(templateData, activities, multiLevel);

  var firstLevel;
  if (rootElements == null || rootElements.length == 0) {
    firstLevel = Object.keys(activitiesBySet);
  } else {
    firstLevel = [...rootElements, "other", "uncategorized"];
  }

  const templateModel = [];

  if (multiLevel) {
    deepLinkToParents(activitiesBySet, firstLevel, activities);

    const templateLinks = {};
    Object.keys(activitiesBySet).forEach((setKey) => {
      let set = getSet(activities, setKey);

      if (
        set != null &&
        activitiesBySet[setKey] != null &&
        activitiesBySet[setKey].length > 0
      ) {
        let group = {
          ...set,
          item_id: setKey,
          item_type: "set",
          children: activitiesBySet[setKey],
          isExpanded: true,
        };
        templateLinks[setKey] = group;
      }
    });

    //find children to link
    linkTree(templateLinks, activitiesBySet, activities);

    Object.values(templateLinks).forEach((group) => {
      if (group.to_link) {
        //link children
        linkNewChildren(group, templateLinks);

        //remove items already present as leaves
        cleanupChildren(group);
      }
    });

    Object.keys(templateLinks).forEach((k) => {
      if (k.endsWith("_misc")) {
        const parent = templateLinks[k.replace("_misc", "")];
        if (parent) {
          const children = parent.children || [];
          var hasChild = false;
          children.forEach((c) => {
            if (c.id == k) hasChild = true;
          });
          if (!hasChild) children.push(templateLinks[k]);
          parent.children = children.filter(
            (e, i, self) =>
              self.findIndex((e2) => e.item_id === e2.item_id) === i,
          );
        }
      }
    });

    const visibleItems = [];
    Object.values(firstLevel).forEach((setKey) => {
      const group = templateLinks[setKey];
      if (group != null && group.item_id !== "other") {
        let addedItems = [];
        addVisible(group, visibleItems, addedItems);
      }
    });
    const oth = templateLinks["other"];
    if (oth != null && oth.children != null) {
      const newChildren = [];
      oth.children.forEach((c) => {
        if (!visibleItems.includes(c.item_id)) newChildren.push(c);
      });
      oth.children = newChildren;
    }

    Object.values(templateLinks).forEach((group) => {
      //compute hours
      group.days = computeGroupTotalHours(group, {});
    });

    Object.values(firstLevel).forEach((setKey) => {
      const group = templateLinks[setKey];
      if (group) templateModel.push(group);
    });
  } else {
    linkToParents(activitiesBySet, firstLevel, activities);

    Object.values(firstLevel).forEach((setKey) => {
      let set = getSet(activities, setKey);

      if (
        set != null &&
        activitiesBySet[setKey] != null &&
        activitiesBySet[setKey].length > 0
      ) {
        let group = {
          ...set,
          item_id: setKey,
          item_type: "set",
          children: activitiesBySet[setKey],
          isExpanded: true,
        };
        group.days = computeGroupTotalHours(group, {});
        templateModel.push(group);
      }
    });
  }

  return templateModel;
};

const linkNewChildren = (group, templateLinks, parsed = []) => {
  if (group && group.to_link) {
    if (parsed.includes(group.item_id)) return;

    const newParsed = parsed.slice(0);
    newParsed.push(group.item_id);

    const children = [];
    group.to_link.forEach((c) => {
      //console.log("link " + c + " to " + group.item_id);
      const child = templateLinks[c];
      if (child) {
        children.push(child);
        linkNewChildren(child, templateLinks, newParsed);
      }
    });

    group.children = children.filter(
      (e, i, self) => self.findIndex((e2) => e.item_id === e2.item_id) === i,
    );
  }
};

const linkTree = (templateLinks, activitiesBySet, activities) => {
  Object.values(activitiesBySet).forEach((c) => {
    c.forEach((a) => {
      let group = {
        ...a,
        item_type: "activity",
        children: activitiesBySet[c],
        isExpanded: true,
      };
      templateLinks[a.item_id] = group;
    });
  });

  Object.values(activities).forEach((act) => {
    if (act && act.parents) {
      act.parents.forEach((p) => {
        const hasData = activitiesBySet[p];
        if (hasData) {
          let pLink = templateLinks[p];
          if (!pLink) {
            const pact = activities[p];
            pLink = {
              ...pact,
              item_id: p,
              item_type: "set",
              children: activitiesBySet[p],
              isExpanded: true,
            };
            templateLinks[p] = pLink;
          }
          pLink.to_link = pLink.to_link || new Set();
          pLink.to_link.add(act.id);
        }
      });
    }
  });
};

const computeGroupTotalHours = (group, selectedGroup) => {
  let days = [{ hours: 0 }, { hours: 0 }];
  days.forEach((day, dayIndex) => {
    day.hours = getGroupHours(selectedGroup, group, dayIndex, 1);
  });

  return days;
};

const getGroupHours = (selectedItem, child, dayIndex, currentLevel, parsed = []) => {
  const group = child;
  if (parsed.includes(group.item_id)) return false;

  const newParsed = parsed.slice(0);
  newParsed.push(group.item_id);

  if (selectedItem.item_id === child.item_id && child.item_type != "set") {
    group.is_modified = true;
    group.days = selectedItem.days;
  }

  if (!hasItems(group.children)) {
    return group.days && group.days[dayIndex] ? group.days[dayIndex].hours : 0;
  }

  if (!group.has_misc && 1 * (group.days && group.days[dayIndex] ? group.days[dayIndex].hours : 0) > 0) {
    group.has_misc = true;
    var cloned = JSON.parse(JSON.stringify(group, (key, value) =>
        key == "children" ? undefined : value,
    ));
    cloned.item_id += "_misc";
    cloned.name += " (Generic)";
    group.children = [...group.children, cloned];
  }

  let total = 0;
  group.children.forEach((childGroup) => {
    const childTotal = getGroupHours(selectedItem, childGroup, dayIndex, currentLevel + 1, newParsed);
    total = total + parseFloat(childTotal);
  });

  return total.toFixed(2);
};

getDailyAdjustment = (timeInterval = "week") => {
  return timeInterval == "week" ? WEEKLY_HOURS / 24 : 1;
};

fitHours = ({
  uncategorizedActivity,
  activities,
  timeInterval,
  fitToUncategorized = false,
  callback = undefined,
}) => {
  const newActivities = activities.filter(
    (a) => a.id_activity != uncategorizedActivity.id_activity,
  );
  let sumModified = 0;
  let sumUnmodified = 0;
  newActivities.forEach((a) => {
    if (a.is_modified) sumModified += 1 * a.total_hours || 0;
    else sumUnmodified += 1 * a.total_hours || 0;
  });

  const redistributeModified =
    sumModified > WEEKLY_HOURS ? WEEKLY_HOURS - sumModified : 0;
  const redistributeUnmodified =
    sumModified > WEEKLY_HOURS
      ? -sumUnmodified
      : WEEKLY_HOURS - sumUnmodified - sumModified;

  const redistributed = [];
  let sumHours = 0;
  newActivities.forEach((a) => {
    const total_hours = 1 * a.total_hours || 0;
    const denominator = a.is_modified ? sumModified : sumUnmodified;
    const weight = denominator > 0 ? total_hours / denominator : 0;
    let hours =
      total_hours +
      weight * (a.is_modified ? redistributeModified : redistributeUnmodified);
    hours = Math.max(0, timeInterval * Math.floor(hours / timeInterval));
    sumHours += hours;

    if (fitToUncategorized) hours = 0;

    redistributed.push({
      id_activity: a.id_activity,
      total_hours: hours,
      hours: hours > 0 ? hours / getDailyAdjustment() : 0,
      target_hours: hours,
      is_modified: !!a.is_modified,
    });
  });

  if (fitToUncategorized) {
    redistributed.push({
      id_activity: uncategorizedActivity.id_activity,
      total_hours: sumHours,
      hours: sumHours > 0 ? sumHours / getDailyAdjustment() : 0,
      target_hours: sumHours,
      is_modified: true,
    });
  }

  const roundingErr = WEEKLY_HOURS - sumHours;
  for (var i = 0; i < redistributed.length; i++) {
    if (!redistributed[i].is_modified || i === redistributed.length - 1) {
      const adjusted = redistributed[i].total_hours + roundingErr;
      redistributed[i].total_hours = adjusted;
      redistributed[i].target_hours = adjusted;
      redistributed[i].hours = adjusted / getDailyAdjustment();
      break;
    }
  }

  if (callback) {
    callback({
      templateActivities: redistributed,
      templateHours: redistributed,
      shouldRefresh: true,
      updateOld: false,
    });
  }
};

_extractElements = (data, elementsData) => {
  if (data != null) {
    data.forEach((it) => {
      if (
        it.item_id != null &&
        (it.children == null || it.children.length === 0)
      ) {
        elementsData[it.item_id] = it;
      }
      _extractElements(it.children, elementsData);
    });
  }
};

mapElementsToTemplateHours = (template_data) => {
  const elementsData = {};
  _extractElements(template_data, elementsData);

  const data = Object.values(elementsData).map((it) => {
    return {
      id_activity: it.item_id,
      hours: it.days ? it.days[0].hours : 0,
      total_hours: it.days ? it.days[1].hours : 0,
      is_modified: it.days
        ? it.days[0].is_modified || it.days[1].is_modified
        : false,
    };
  });

  return data;
};

getUncategorizedElement = (controller, entity = "elements") => {
  const pattern = new RegExp("[Uu]ncategorized".split(" ").join("|"), "gi");

  // Find uncategorized
  const source = controller.state[entity];
  const uncategorizedElement = find(source, (a) => {
    return a.name && a.name.match(pattern);
  });

  return uncategorizedElement && uncategorizedElement.id
    ? {
        is_uncategorized: true,
        id_activity: uncategorizedElement.id,
        total_hours: 0,
        hours: 0,
      }
    : {};
};

function hasVisibleParent(element, elements) {
  const firstParent = hasItems(element.parents)
    ? elements[element.parents[0].replace("_misc", "")]
    : null;
  const hasVisibleParent =
    firstParent != null &&
    (element.parents.length > 1 || firstParent.name !== "Hidden");
  return hasVisibleParent;
}

// ------------------------------------- Elements -------------------------------------
const _getSpecialParents = (elements, routineElements) => {
  let orphans = { uncategorized: [], other: [] };
  routineElements.forEach((re) => {
    const element = elements[re.id_activity.replace("_misc", "")] || {};
    const hasVisibleParent = hasVisibleParent(element, elements);
    if (!hasVisibleParent && element.is_major == 0 && element.is_core == 0) {
      if (re.is_uncategorized) {
        orphans.uncategorized.push(re.id_activity);
      } else {
        orphans.other.push(re.id_activity);
      }
    }
  });

  return [
    {
      id: "other",
      name: "other",
      color: AppTheme.almostBlack,
      icon: "2753",
      days: [{ hours: 0 }, { hours: 0 }],
      children: orphans.other,
    },
    {
      id: "uncategorized",
      name: "uncategorized",
      color: AppTheme.almostBlack,
      icon: "2753",
      days: [{ hours: 0 }, { hours: 0 }],
      children: orphans.uncategorized,
    },
  ];
};

const _getChildElementModel = (element, hours) => {
  if (!element || !hours) return null;

  return {
    item_type: element.children ? "tag" : "activity",
    item_id: element.id,
    name: element.name,
    color: element.color,
    icon: element.icon,
    is_uncategorized: !!hours.is_uncategorized,
    is_modified: !!hours.is_modified,
    days: [
      { hours: hours.hours || 0, is_modified: !isTotalHours(hours), index: 0 },
      {
        hours: hours.total_hours || 0,
        is_modified: isTotalHours(hours),
        index: 1,
      },
    ],
  };
};

_getMajorElements = (element, elementsHash, hoursHash) => {
  let group = {
    ...element,
    item_id: element.id,
    item_type: "set",
    isExpanded: true,
    children: _getChildElements(element.children, elementsHash, hoursHash),
  };

  return group;
};

_getChildElements = (childrenIds, elements, hours) => {
  if (!hasItems(childrenIds)) return [];

  let children = [];
  childrenIds.forEach((childId) => {
    const element = elements[childId.replace("_misc", "")];
    const elementHours = hours[childId];
    const elementModel = _getChildElementModel(element, elementHours);

    if (elementModel) {
      if (hasItems(element.children)) {
        elementModel.children = _getChildElements(
          element.children,
          elements,
          hours,
        );
      }
      children.push(elementModel);
    }
  });

  return children.sort((a, b) => (a.days[0].hours < b.days[0].hours ? 1 : -1));
};

const getElementsHierarchy = (controller, routineElements = []) => {
  const uncategorized = getUncategorizedElement(controller);
  const parentsHash = {};
  const elementsHash = {};
  controller.state.elements.forEach((elem) => {
    if (elem.is_major == 1 || elem.is_core == 1) parentsHash[elem.id] = elem;
    elementsHash[elem.id] = elem;
  });

  const hoursHash = {};
  routineElements.forEach((el) => {
    if (el.id_activity == uncategorized?.id_activity) {
      el.is_uncategorized = true;
    }
    hoursHash[el.id_activity] = el;
  });

  const parents = [];
  const topElements = [
    ...Object.values(parentsHash),
    ..._getSpecialParents(elementsHash, routineElements),
  ];
  topElements.forEach((p) => {
    let group = _getMajorElements(p, elementsHash, hoursHash);
    if (hasItems(group.children)) {
      parents.push(group);
    }
  });

  return parents;
};

const LastWeekHelper = {
  WEEKLY_HOURS,
  getTemplateDataModel,
  getElementsHierarchy,
  computeGroupTotalHours,
  getGroupHours,
  isTotalHours,
  fitHours,
  getDailyAdjustment,
  mapElementsToTemplateHours,
  getUncategorizedElement,
};

export default LastWeekHelper;
